import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { I18nService } from 'i18n';
import { AuthService } from 'auth';
import { CommunityMessagesService } from '../../../services/community-messages.service';
import { CommunityUnreadService } from '../../../services/community-unread.service';
import { CommunityMessage } from '../../../models/community-messages.models';
import { communityErrorKey } from '../../../models/community.models';
import { CommunityAvatarComponent } from '../../../components/community-avatar/community-avatar.component';

/**
 * One message with its parent thread (`community/messages/:id`, Phase 3).
 * `GET /messages/:id` marks it read as a side effect when the viewer is the
 * recipient, so loading it is enough to clear the unread badge; `refresh()`
 * afterwards keeps the shell's count in step without waiting for the poll.
 */
@Component({
  selector: 'app-community-message-detail-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslocoModule, CommunityAvatarComponent],
  templateUrl: './community-message-detail-page.component.html',
  styleUrl: './community-message-detail-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityMessageDetailPageComponent implements OnInit, OnDestroy {
  private messagesService = inject(CommunityMessagesService);
  private unread = inject(CommunityUnreadService);
  private auth = inject(AuthService);
  private i18n = inject(I18nService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  message = signal<CommunityMessage | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);
  busy = signal(false);

  replyOpen = signal(false);
  replyDraft = '';
  replyBusy = signal(false);
  replyError = signal<string | null>(null);

  private routeSub?: Subscription;

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(params => {
      const id = Number(params.get('id'));
      if (id) this.load(id);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  get myUserId(): number | null {
    return this.auth.getUser()?.id ?? null;
  }

  backLink(): string {
    return `/${this.lang}/community/messages`;
  }

  /** Full parent chain (oldest first) plus the message itself. */
  fullThread(): CommunityMessage[] {
    const m = this.message();
    if (!m) return [];
    return [...(m.thread ?? []), m];
  }

  isMine(m: CommunityMessage): boolean {
    return m.sender.id === this.myUserId;
  }

  memberLink(userId: number): string {
    return `/${this.lang}/community/u/${userId}`;
  }

  load(id: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.messagesService.get(id).subscribe({
      next: res => {
        this.message.set(res.message);
        this.loading.set(false);
        this.unread.refresh();
      },
      error: err => {
        this.loading.set(false);
        this.error.set(err?.status === 404 ? 'community.messages.not_found' : communityErrorKey(err));
      }
    });
  }

  openReply(): void {
    this.replyDraft = '';
    this.replyError.set(null);
    this.replyOpen.set(true);
  }

  cancelReply(): void {
    this.replyOpen.set(false);
  }

  submitReply(): void {
    const m = this.message();
    const content = this.replyDraft.trim();
    if (!m || !content) return;
    this.replyBusy.set(true);
    this.replyError.set(null);
    this.messagesService.reply(m.id, content).subscribe({
      next: res => {
        this.replyBusy.set(false);
        this.replyOpen.set(false);
        this.router.navigate([`/${this.lang}/community/messages/${res.message.id}`]);
      },
      error: err => {
        this.replyBusy.set(false);
        this.replyError.set(
          CommunityMessagesService.isRecipientUnreachable(err) ? 'community.messages.errors.recipient_unreachable' : communityErrorKey(err)
        );
      }
    });
  }

  toggleArchive(): void {
    const m = this.message();
    if (!m) return;
    this.busy.set(true);
    this.messagesService.archive(m.id, !m.is_archived).subscribe({
      next: () => {
        this.busy.set(false);
        this.message.update(current => (current ? { ...current, is_archived: !current.is_archived } : current));
      },
      error: err => {
        this.busy.set(false);
        this.error.set(communityErrorKey(err));
      }
    });
  }
}
